/*
 * "Apply bundle discount" — rewrites eligible cart lines' item properties so
 * Rebuy's Shopify Functions bundle discount (tiered by item count) prices them
 * exactly as the bundle page's own "Add Bundle To Cart" does. Eligible PRODUCT
 * IDS are computed server-side in snippets/bundle-discount-apply.liquid; all
 * lines get ONE shared session id so the function tiers them together.
 *
 * Lines are re-identified by product id on EVERY pass, never by a captured
 * line key: a line's `key` hashes its discount state, so re-tagging one line
 * re-keys its old session partners the moment their discount drops.
 */
if (!customElements.get('bundle-discount-apply')) {
  class BundleDiscountApply extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-bundle-apply-button]');
      if (this.button) this.button.addEventListener('click', this.apply.bind(this));
    }

    newSessionId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      return `bb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    async apply() {
      if (this.applying || !this.button) return;
      this.applying = true;
      this.classList.add('is-loading');
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');

      let eligibleProducts = [];
      try {
        eligibleProducts = JSON.parse(this.dataset.eligibleProducts || '[]');
      } catch (error) {
        eligibleProducts = [];
      }
      const eligible = new Set(eligibleProducts);
      const bundleProperties = {
        _r_bbsi: 0,
        _r_added_bundle_session_id: this.newSessionId(),
        _source: 'Rebuy',
        _widget_id: this.dataset.widgetId,
        _attribution: 'Rebuy Bundle Builder'
      };

      let failed = false;
      try {
        let cart = await fetch(`${routes.cart_url}.js`).then(response => response.json());
        let misses = 0;
        // One line per pass until every eligible line carries the new session
        // id. The change response feeds the next pass, so re-keyed and merged
        // lines are always seen fresh. The pass cap is a runaway guard only.
        for (let pass = 0; pass < 40; pass++) {
          const line = cart.items.find(item =>
            eligible.has(item.product_id) &&
            (!item.properties || item.properties._r_added_bundle_session_id !== bundleProperties._r_added_bundle_session_id)
          );
          if (!line) break;
          const properties = Object.assign({}, line.properties, bundleProperties);
          const updated = await this.changeLine(line.key, line.quantity, properties);
          if (updated) {
            cart = updated;
            misses = 0;
          } else {
            // Stale key or transient failure — refetch and rescan. Two strikes
            // in a row without progress means it's a real failure.
            misses += 1;
            if (misses >= 2) {
              failed = true;
              break;
            }
            cart = await fetch(`${routes.cart_url}.js`).then(response => response.json());
          }
        }
      } catch (error) {
        failed = true;
      }

      if (document.getElementById('main-cart-items')) {
        // The cart page's section refresh only re-renders its items grid — reload
        // so the summary (and this component's visibility) update too.
        window.location.reload();
        return;
      }

      await this.refreshCartDrawer();
      this.applying = false;
      if (failed) {
        document.querySelectorAll('bundle-discount-apply [data-bundle-apply-error]').forEach(error => {
          error.hidden = false;
        });
      }
    }

    async changeLine(key, quantity, properties) {
      // quantity is REQUIRED: /cart/change.js resets an omitted quantity to 1.
      // Retries cover transient failures (429 bursts, proxy hiccups); a 404
      // means the key went stale mid-flight, which retrying can't fix — the
      // caller refetches the cart and rescans instead.
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const response = await fetch(routes.cart_change_url, {
            ...fetchConfig(),
            ...{ body: JSON.stringify({ id: key, quantity, properties }) }
          });
          if (response.ok) return response.json();
          if (response.status === 404) return null;
        } catch (error) {
          // fall through to backoff
        }
        await new Promise(resolve => setTimeout(resolve, 400 * (attempt + 1)));
      }
      return null;
    }

    async refreshCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      try {
        const sections = drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map(section => section.section)
          : ['cart-drawer', 'cart-counter'];
        const rendered = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`).then(response => response.json());
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents({ sections: rendered }, false);
        }
        if (typeof updateFreeShipping === 'function') updateFreeShipping();
        if (drawer && drawer.classList.contains('is-visible')) {
          const inner = drawer.querySelector('.cart-drawer__inner');
          if (inner) inner.focus();
        }
      } catch (error) {
        window.location.reload();
      }
    }
  }
  customElements.define('bundle-discount-apply', BundleDiscountApply);
}
